import { CustomListKey, HomeCareLeadId, ILocation, RegionId } from "@sp-crm/core";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
    HoveredMapEntity,
    MapMarkerId,
} from "components/community-search/community-map/types";
import { SearchResultsMap } from "components/map/search-results-map";
import { MapSearchResults, MapView } from "components/map/types";
import { trpc } from "components/rpc/trpc";
import "maplibre-gl/dist/maplibre-gl.css";
import React, { useCallback, useMemo } from "react";
import { useCustomList } from "store/selectors/bridge";
import { HomeCareLeadRecord } from "./client-map-card-list";
import { ClientMapPinActions } from "./client-map-pin-actions";
import { ClientMapSearchParams, toGeoParamsInput } from "./geo-params";

const staleTime = 1000 * 60 * 10;

const emptyMapView: MapView = {
    zoomLevel: null,
    bounds: null,
    viewportHeight: 0,
    viewportWidth: 0,
};

interface ClientMapSearchResultsMapProps {
    hoveredClient: null | HoveredMapEntity;
    searchParams: ClientMapSearchParams;
    regionId: RegionId;
    leads: HomeCareLeadRecord[];
    onExplicitBoundsChanged: (northWest: ILocation, southEast: ILocation) => void;
}

export const ClientMapSearchResultsMap: React.FC<
    ClientMapSearchResultsMapProps
> = props => {
    const { hoveredClient, searchParams, regionId, leads, onExplicitBoundsChanged } =
        props;
    const [mapView, setMapView] = React.useState<MapView | null>(null);
    const { customList } = useCustomList(CustomListKey.HomeCareLeadStatus);

    const statusListItemIdById = useMemo(
        () => new Map(leads.map(lead => [lead.id, lead.statusListItemId])),
        [leads],
    );

    const trpcClient = trpc.useTRPC();
    const query = useQuery({
        ...trpcClient.homeCareLead.mapSearch.queryOptions({
            searchString: searchParams.searchString,
            searchType: searchParams.searchType,
            assignedUserId: searchParams.assignedUserId,
            statusListItemIds: searchParams.statusListItemIds,
            geoParams: toGeoParamsInput(searchParams.geoParams),
            regionId,
            mapView: mapView ?? emptyMapView,
        }),
        staleTime,
        placeholderData: keepPreviousData,
        enabled: !!mapView && !!customList,
    });

    const searchResults: MapSearchResults = useMemo(() => {
        if (!query.data) {
            return null;
        }

        return {
            entityClusters: query.data.clusters.map(c => ({
                lat: c.lat,
                lng: c.lng,
                count: c.count,
            })),
            entityCoordinates: query.data.coordinates.map(coordinate => ({
                id: coordinate.id as MapMarkerId,
                lat: coordinate.lat,
                lng: coordinate.lng,
                name: coordinate.name,
                color: "violet",
                appLink: coordinate.appLink,
            })),
            searchCoordinates: query.data.searchCoordinates,
            recommendedZoom: query.data.recommendedZoom,
        };
    }, [query.data]);

    const renderMarkerControl = useCallback(
        (id: MapMarkerId) => (
            <ClientMapPinActions
                leadId={id as HomeCareLeadId}
                statusListItemId={statusListItemIdById.get(id as HomeCareLeadId) ?? null}
            />
        ),
        [statusListItemIdById],
    );

    return (
        <SearchResultsMap
            explicitBounds={searchParams.geoParams?.bounds || null}
            geoSearchKey={JSON.stringify(searchParams.geoParams ?? null)}
            onExplicitBoundsChanged={onExplicitBoundsChanged}
            hoveredEntity={hoveredClient}
            isLoading={query.isLoading}
            basemapPolicy="selfHostedOnly"
            onMapViewChanged={setMapView}
            results={searchResults}
            renderMarkerControl={renderMarkerControl}
            error={query.error}
        />
    );
};
